import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { defaultPagination, Pagination } from 'src/models/pagination';
import { defaultStatus, SliceStatus } from 'src/models/status';
import type { User } from 'src/models/user.interface';
import { defaultUserFilterState, UserFilter } from 'src/models/userFilter';
import type { AppThunk } from 'src/store';
import { resetFilters } from 'src/store/extraActions';
import axiosCore from 'src/utils/axiosCore';

interface UserState {
  users: User[];
  pagination: Pagination;
  filter: UserFilter;
  reload: boolean;
  status: SliceStatus;
}

const initialState: UserState = {
  users: [],
  filter: defaultUserFilterState,
  pagination: defaultPagination,
  reload: false,
  status: defaultStatus
};

// This is used in internal user management only.
const slice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    getUsers(
      state: UserState,
      action: PayloadAction<{ users: User[]; pagination: Pagination }>
    ) {
      const { users, pagination } = action.payload;
      state.users = users;
      state.pagination = pagination;
      state.reload = false;
      state.status = { ...defaultStatus };
    },
    updateFilter(
      state: UserState,
      action: PayloadAction<{ filter: UserFilter }>
    ) {
      const { filter } = action.payload;
      state.filter = { ...state.filter, page: 1, ...filter };
    },
    requestFailed(state: UserState, action: PayloadAction<string>) {
      state.status = { message: action.payload, variant: 'error' };
    }
  },
  extraReducers: {
    // resets this slice when changing users
    [resetFilters.type]() {
      return initialState;
    }
  }
});

export const reducer = slice.reducer;

export const getUsers =
  (filter: UserFilter): AppThunk =>
  async (dispatch) => {
    let response;
    try {
      response = await axiosCore.post<{ data: any }>(`/user/filter`, filter);
    } catch (error) {
      dispatch(slice.actions.requestFailed(error.message));
      return;
    }
    dispatch(slice.actions.getUsers(response.data.data));
  };

export const updateUserFilter =
  (filter: UserFilter): AppThunk =>
  async (dispatch) => {
    dispatch(slice.actions.updateFilter({ filter }));
  };
